import { CredentialLine } from '@/components/landing/credential-line';
import { Plate } from '@/components/landing/primitives';
import { stagger } from '@/components/landing/stagger';
import { STACKS, type StackId } from '@/components/landing/inference-layers';
import { credential } from '@/content/credentials';

/**
 * Who supplies the parts, as two large panels: the official marks, what each
 * supplies, and the credentials as statements along the bottom edge, so both
 * panels end on the same line.
 *
 * Marks are the owner-supplied official artwork only: the Qwen mark (Qwen
 * media kit, reversed white), the Alibaba Cloud logo, and the NVIDIA Inception
 * Program lockup — the one form in which the NVIDIA logo appears on the site.
 * The credential sentences below repeat no logo.
 */
interface Mark {
  src: string;
  alt: string;
  width: number;
  height: number;
  className: string;
}

const MARKS: Record<StackId, Mark[]> = {
  qwen: [
    { src: '/img/qwen-logo.svg', alt: 'Qwen', width: 868, height: 232, className: 'h-10 md:h-12' },
    { src: '/img/alibaba-cloud.webp', alt: 'Alibaba Cloud', width: 480, height: 212, className: 'h-14 md:h-16' },
  ],
  nvidia: [
    { src: '/img/nvidia-inception.webp', alt: 'NVIDIA Inception Program', width: 560, height: 190, className: 'h-14 md:h-[4.5rem]' },
  ],
};

export function StacksSection() {
  return (
    <Plate eyebrow="The stack" id="stacks" labelledBy="stacks-title">
      <h2 id="stacks-title" className="lp-h2 lp-reveal max-w-4xl" style={stagger(1)}>
        Built on Qwen and NVIDIA
      </h2>
      <p className="lp-body lp-reveal mt-6 max-w-2xl" style={stagger(2)}>
        Qwen models are the brain. NVIDIA computers are the engine. We put the two together and tune them
        for your work.
      </p>

      <div className="mt-16 grid gap-5 md:mt-20 md:grid-cols-2">
        {STACKS.map((s, i) => (
          <article key={s.id} aria-labelledby={`${s.id}-title`} className="lp-reveal stack-panel" style={stagger(3 + i)}>
            <p className="eyebrow">{s.role}</p>
            <h3 id={`${s.id}-title`} className="mt-8 flex min-h-[4.5rem] flex-wrap items-center gap-x-6 gap-y-4">
              {MARKS[s.id].map((m, j) => (
                <span key={m.src} className="flex items-center gap-6">
                  {j > 0 && (
                    <span className="text-2xl font-light text-muted-foreground" aria-hidden>
                      +
                    </span>
                  )}
                  <img
                    loading="lazy"
                    decoding="async"
                    src={m.src}
                    alt={m.alt}
                    width={m.width}
                    height={m.height}
                    className={`${m.className} w-auto`}
                  />
                </span>
              ))}
            </h3>

            <p className="mt-8 text-[17px] leading-relaxed text-[hsl(var(--fg-secondary))]">{s.line}</p>

            <ul className="mt-10 space-y-3 border-t border-border pt-8 md:mt-auto" aria-label={`${s.name} credentials`}>
              {s.credentials.map((id) => (
                <li key={id} className="text-lg font-medium leading-snug text-foreground md:text-xl">
                  <CredentialLine c={credential(id)} form="long" artwork={false} />
                </li>
              ))}
            </ul>
          </article>
        ))}
      </div>
    </Plate>
  );
}
